import { useQuery, useMutation } from "@tanstack/react-query";
import { fetchApiGet, fetchApiPost } from "./utils";
import type {
  MarketDataRow,
  SimilarityResponse,
  UserInputValues,
} from "../utils/marketSimilarity/types";
import { toTableRows } from "../utils/marketSimilarity/tableUtils";

export interface FetchHistoricInputsRequest {
  dateString: string; // YYYY-MM-DD
  commodityKey: string;
  numRallyDays: number;
}

export type HistoricInputsResponse = Omit<
  MarketDataRow,
  "close" | "contAdjustedClose" | "rallyScores" | "lighthouseCorrelScores" | "date"
>;

const emptySimilarityResponse: SimilarityResponse = {
  rawData: [],
  clusteredData: [],
  lineChart: [],
};

/**
 * Hook to fetch commodity keys
 */
export function useListCommodityKeys() {
  const queryParams = new URLSearchParams();

  return useQuery({
    queryKey: ["marketSimilarity", "commodityKeys"],
    queryFn: async () => {
      const result = await fetchApiGet<{ data: string[] }>(
        "market_similarity/fetch_commodity_keys",
        queryParams
      );
      return result;
    },
    retry: 1,
  });
}



/**
 * Hook to fetch similarity data
 */
export function useFetchSimilarityData() {
  return useMutation({
    mutationFn: async (inputValues: UserInputValues) => {
      // Build threshold_affects array based on cluster config
      const threshold_affects = inputValues.clusterThreshold != null
        ? [{
            type: inputValues.clusterBy === "euclidean" ? "euclidean" as const : "lighthouseCorrel" as const,
            threshold: inputValues.clusterThreshold
          }]
        : [];

      const payload = {
        dateString: inputValues.dateString,
        date: inputValues.date,
        dateWeight: inputValues.dateWeight,
        ts2vecDate: inputValues.ts2vecDate,
        ts2vecWeight: inputValues.ts2vecWeight,
        stxUseScore: inputValues.stxUseScore,
        stxUseScoreWeight: inputValues.stxUseScoreWeight,
        seasonalScoreV1: inputValues.seasonalScoreV1,
        seasonalScoreV1Weight: inputValues.seasonalScoreV1Weight,
        seasonalScoreV2: inputValues.seasonalScoreV2,
        seasonalScoreV2Weight: inputValues.seasonalScoreV2Weight,
        mmScore: inputValues.mmScore,
        mmScoreWeight: inputValues.mmScoreWeight,
        algoScoreV3: inputValues.algoScoreV3,
        algoScoreV3Weight: inputValues.algoScoreV3Weight,
        numRallyDays: inputValues.numRallyDays,
        rallyPercentage: inputValues.rallyPercentage,
        rallyScoreWeight: inputValues.rallyScoreWeight,
        rsi: inputValues.rsi,
        rsiWeight: inputValues.rsiWeight,
        commodityKey: inputValues.commodityKey,
        threshold_affects,
      };

      const result = await fetchApiPost<SimilarityResponse>(
        "market_similarity/fetch_similarity_data",
        payload
      );

      return result;
    },
  });
}

/**
 * Hook to fetch historic inputs for a given date and commodity
 */
export function useFetchHistoricInputs() {
  return useMutation({
    mutationFn: async (request: FetchHistoricInputsRequest) => {
      const result = await fetchApiPost<HistoricInputsResponse>(
        "market_similarity/fetch_historic_inputs",
        request
      );

      return result;
    },
  });
}
